/**
 * Admin functions for managing front-facing building polygon coordinates
 * Used in the Front Facing Tracer tool for updating unit front-facing polygon coordinates
 */
import { supabase } from '@/integrations/supabase/client';
import { trackEvent } from '@/lib/analytics';
import type { MapCoordinates } from '@/types/map';
import type { Json } from '@/integrations/supabase/types';

export interface FrontFacingPolygon {
  id: string;
  block: 'A' | 'B';
  unit_number: string;
  unit_id: string;
  map_coordinates: MapCoordinates;
  view_type: string;
  created_at: string;
  updated_at: string;
}

/**
 * Create a new front-facing polygon
 */
export async function createFrontFacingPolygon(
  unitId: string,
  coordinates: MapCoordinates,
  viewType: string = 'front'
): Promise<{ success: boolean; error?: string; data?: FrontFacingPolygon }> {
  try {
    // First, get the unit to extract block and unit_number
    const { data: unit, error: unitError } = await supabase
      .from('property_units')
      .select('block, unit_number')
      .eq('id', unitId)
      .single();

    if (unitError || !unit) {
      console.error('Error fetching unit:', unitError);
      return { success: false, error: unitError?.message || 'Unit not found' };
    }

    const { data, error } = await (supabase as any)
      .from('front_facing_polygons')
      .insert({
        block: unit.block,
        unit_number: unit.unit_number,
        unit_id: unitId,
        map_coordinates: coordinates as unknown as Json,
        view_type: viewType,
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating front-facing polygon:', error);
      return { success: false, error: error.message };
    }

    trackEvent('admin_front_facing_polygon_created', {
      unit_id: unitId,
      unit_number: unit.unit_number,
      block: unit.block,
      view_type: viewType,
      point_count: coordinates.points?.length || 0,
    });

    return { success: true, data: data as FrontFacingPolygon };
  } catch (error: unknown) {
    console.error('Error creating front-facing polygon:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    return { success: false, error: errorMessage };
  }
}

/**
 * Update an existing front-facing polygon
 */
export async function updateFrontFacingPolygon(
  polygonId: string,
  coordinates: MapCoordinates
): Promise<{ success: boolean; error?: string; data?: FrontFacingPolygon }> {
  try {
    const { data, error } = await (supabase as any)
      .from('front_facing_polygons')
      .update({
        map_coordinates: coordinates as unknown as Json,
      })
      .eq('id', polygonId)
      .select()
      .single();

    if (error) {
      console.error('Error updating front-facing polygon:', error);
      return { success: false, error: error.message };
    }

    trackEvent('admin_front_facing_polygon_updated', {
      polygon_id: polygonId,
      point_count: coordinates.points?.length || 0,
    });

    return { success: true, data: data as FrontFacingPolygon };
  } catch (error: unknown) {
    console.error('Error updating front-facing polygon:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    return { success: false, error: errorMessage };
  }
}

/**
 * Get all front-facing polygons, optionally filtered by block or unit number
 */
export async function getFrontFacingPolygons(
  block?: 'A' | 'B',
  unitNumber?: string
): Promise<{ success: boolean; error?: string; data?: FrontFacingPolygon[] }> {
  try {
    let query = (supabase as any)
      .from('front_facing_polygons')
      .select('*')
      .order('block', { ascending: true })
      .order('unit_number', { ascending: true });

    if (block) {
      query = query.eq('block', block);
    }

    if (unitNumber) {
      query = query.eq('unit_number', unitNumber);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching front-facing polygons:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data: (data || []) as FrontFacingPolygon[] };
  } catch (error: unknown) {
    console.error('Error fetching front-facing polygons:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    return { success: false, error: errorMessage };
  }
}

/**
 * Get a front-facing polygon by unit ID and view type
 */
export async function getFrontFacingPolygonByUnitId(
  unitId: string,
  viewType: string = 'front'
): Promise<{ success: boolean; error?: string; data?: FrontFacingPolygon }> {
  try {
    const { data, error } = await (supabase as any)
      .from('front_facing_polygons')
      .select('*')
      .eq('unit_id', unitId)
      .eq('view_type', viewType)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No rows returned
        return { success: true, data: undefined };
      }
      console.error('Error fetching front-facing polygon:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data: data as FrontFacingPolygon };
  } catch (error: unknown) {
    console.error('Error fetching front-facing polygon:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    return { success: false, error: errorMessage };
  }
}

/**
 * Delete a front-facing polygon
 */
export async function deleteFrontFacingPolygon(
  polygonId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await (supabase as any)
      .from('front_facing_polygons')
      .delete()
      .eq('id', polygonId);

    if (error) {
      console.error('Error deleting front-facing polygon:', error);
      return { success: false, error: error.message };
    }

    trackEvent('admin_front_facing_polygon_deleted', {
      polygon_id: polygonId,
    });

    return { success: true };
  } catch (error: unknown) {
    console.error('Error deleting front-facing polygon:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    return { success: false, error: errorMessage };
  }
}

